import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  create,
  fetch,
  get,
  WorkersRequest,
  WorkersRequestCreationPayload,
  WorkersRequestUpdatePayload,
  update,
  CreatePostPayload,
  createPost,
  UpdatePostPayload,
  updatePost,
  deletePost,
  DeletePostPayload,
  assignWorkerToPost,
  AssignWorkerToPostPayload,
  count,
  assignWorkersToPosts,
  AssignWorkersToPostsPayload,
} from '../api/workers-requests'
import useStore from '../store'
import { FiltersResourcesEnum } from '../store/filters'
import { Mission } from '../api/missions'

export const workersRequestKeys = {
  create: ['workers-requests', 'create'] as const,
  update: ['workers-requests', 'update'] as const,
  createPost: ['workers-requests', 'create-post'] as const,
  updatePost: ['workers-requests', 'update-post'] as const,
  deletePost: ['workers-requests', 'delete-post'] as const,
  assignWorkersToPosts: ['workers-requests', 'assign-workers-to-posts'] as const,
} as const

//Queries
export const useWorkersRequestQuery = (id?: string): UseQueryResult<WorkersRequest, any> =>
  useResource<WorkersRequest>('workers-requests', get, id)

export const useWorkersRequestOfMissionQuery = (
  missionId?: Mission['_id'],
): UseQueryResult<WorkersRequest[], any> =>
  useResources<WorkersRequest[], any>('workers-requests', fetch, {
    'posts.missions': missionId,
  })

export const useWorkersRequestsCountQuery = (
  filters?: any,
  options?: QueryOptions<number>,
): UseQueryResult<number, any> => {
  return useResources<number, any>(
    'workers-requests/count',
    count,
    filters || useStore(state => state.filters[FiltersResourcesEnum.workersRequests]),
    options,
  )
}

export const useWorkersRequestsQuery = (
  filters?: any,
  options?: QueryOptions<WorkersRequest[]>,
): UseQueryResult<WorkersRequest[], any> => {
  return useResources<WorkersRequest[], any>(
    'workers-requests',
    fetch,
    filters || useStore(state => state.filters[FiltersResourcesEnum.workersRequests]),
    options,
  )
}

//Mutations
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['workers-requests'])
  queryClient.invalidateQueries(['workers'])
}

export const useCreateMutation = (): UseMutationResult<
  WorkersRequest,
  WorkersRequestCreationPayload
> => useMutation(create, { mutationKey: workersRequestKeys.create, onSuccess: onMutationSuccess })

export const useUpdateMutation = (): UseMutationResult<
  WorkersRequest,
  WorkersRequestUpdatePayload
> =>
  useMutation(update, {
    mutationKey: workersRequestKeys.update,
    onSuccess: onMutationSuccess,
  })

export const useCreatePost = (): UseMutationResult<WorkersRequest, CreatePostPayload> =>
  useMutation(createPost, {
    mutationKey: workersRequestKeys.createPost,
    onSuccess: onMutationSuccess,
  })

export const useUpdatePost = (): UseMutationResult<WorkersRequest, UpdatePostPayload> =>
  useMutation(updatePost, {
    mutationKey: workersRequestKeys.updatePost,
    onSuccess: onMutationSuccess,
  })

export const useDeletePost = (): UseMutationResult<WorkersRequest, DeletePostPayload> =>
  useMutation(deletePost, {
    mutationKey: workersRequestKeys.deletePost,
    onSuccess: onMutationSuccess,
  })

export const useAssignWorkerToPost = (): UseMutationResult<
  WorkersRequest,
  AssignWorkerToPostPayload
> =>
  useMutation(assignWorkerToPost, {
    mutationKey: workersRequestKeys.updatePost,
    onSuccess: onMutationSuccess,
  })

export const useAssignWorkersToPosts = (): UseMutationResult<
  WorkersRequest,
  AssignWorkersToPostsPayload
> =>
  useMutation(assignWorkersToPosts, {
    mutationKey: workersRequestKeys.assignWorkersToPosts,
    onSuccess: onMutationSuccess,
  })
